import {
  Autocomplete,
  AutocompleteGetTagProps,
  AutocompleteRenderInputParams,
  CircularProgress,
  ListItemProps,
  Paper,
} from "@mui/material"
import React, { FormEvent, HTMLAttributes, KeyboardEvent, SyntheticEvent, useState } from "react"
import styled, { css, ThemeProvider } from "styled-components"
import { CommonPopperProps } from "./CommonPopperProps"
import { Popper } from "./Popper"
import { PopperSaveButton } from "./PopperSaveButton"
import { PopperTextInput } from "./PopperTextInput"
import { PopperDarkTheme, PopperLightTheme } from "./popperThemes"
import { Chip } from "../../atoms/chip"
import { useDropdownNotFoundHook } from "./useDropdownNotFoundHook"
import { theme } from "../../../theme"

export type CommonDropdownPopperProps<TValue> = CommonPopperProps & {
  value?: TValue
  confirmText: string
  options: TValue[]
  inputLabel?: string
  helperText?: string
  onSubmit?: (newValue: TValue[]) => Promise<void> | void
  onChange?: (newValue: TValue[]) => Promise<void> | void
  onInputChange?: (v: string) => Promise<void> | void
  isSaveButtonInsideInputField?: boolean
  showNotFoundWithMessage?: ShowNotFoundWithMessage<TValue>
  loading?: boolean
  disableClearable?: boolean
}

export type ShowNotFoundWithMessage<TValue> = {
  notFoundText: string
  notFoundLinkText: string
  notFoundPopper: JSX.Element
  onAdd: () => Promise<TValue | undefined>
}

export type EnableDropdownMultiselect<TValue> =
  | {
      multiple?: false
    }
  | {
      multiple: true
      renderTag?: (option: TValue, tagProps: AutocompleteGetTagProps) => JSX.Element
    }

export type DropdownCustomPopperProps<TValue = string> = CommonDropdownPopperProps<TValue> &
  EnableDropdownMultiselect<TValue> & {
    formatOption?: (option: TValue) => string
    renderOption?: (option: TValue) => JSX.Element
    startAdornment?: React.ReactNode
    minHeight?: string
  }

export function DropdownPopper<DropdownT = string>(props: DropdownCustomPopperProps<DropdownT>) {
  const {
    lightMode = false,
    popperTitle,
    onClose,
    onChange,
    onDelete,
    anchorEl,
    className,
    anchorOrigin,
    transformOrigin,
    value,
    multiple,
    loading,
    onInputChange,
    disableClearable = false,
    minHeight,
  } = props

  const [selectedValues, setSelectedValues] = useState<DropdownT[]>([])

  const dropdownNotFoundHook = useDropdownNotFoundHook<DropdownT>(
    props.showNotFoundWithMessage as ShowNotFoundWithMessage<DropdownT>,
    selectedValues,
    setSelectedValues,
    onChange,
    multiple
  )
  const internalOnClose = () => {
    onClose()
    setSelectedValues([])
    if (dropdownNotFoundHook?.isAddNew) {
      // Added delay for better ux behaviour. this way the popper definitely closes before resetting the state
      new Promise((r) => setTimeout(r, 300)).then((r) => dropdownNotFoundHook.setIsAddNew(false))
    }
  }

  const renderDropdown = () => {
    const {
      helperText,
      options = [],
      inputLabel,
      onSubmit,
      confirmText,
      renderOption,
      isSaveButtonInsideInputField = false,
      startAdornment,
    } = props

    const formatOption = props.formatOption
      ? (props.formatOption as (o: DropdownT) => string)
      : (option: DropdownT) => option as string

    const getOptionLabel = (o: DropdownT) => formatOption(o)

    const submit = onSubmit && (onSubmit as (s: DropdownT[]) => void)
    const handleSubmit = (e: React.MouseEvent<Element, MouseEvent> | FormEvent) => {
      if (selectedValues.length) {
        e.preventDefault()
        submit && submit(selectedValues)
      }
    }

    const internalRenderOption = renderOption
      ? (itemProps: ListItemProps, option: DropdownT) => <li {...itemProps}>{renderOption(option)}</li>
      : undefined

    const renderPaper = (paperOptions: HTMLAttributes<HTMLElement>) => {
      const renderAdditionalDropdownContent = () => {
        return (
          <>
            {dropdownNotFoundHook?.renderNotFoundDropdownMessage &&
              dropdownNotFoundHook.renderNotFoundDropdownMessage()}
          </>
        )
      }

      return (
        <CustomPaper $isLightMode={lightMode}>
          {paperOptions.children}
          {renderAdditionalDropdownContent()}
        </CustomPaper>
      )
    }

    const renderInput = (params: AutocompleteRenderInputParams) => {
      const onInputChangeInternal = (event: React.ChangeEvent<{ value: string }>) => {
        onInputChange && onInputChange(event.currentTarget.value)
      }

      if (isSaveButtonInsideInputField) {
        const handleKeyPress = (e: KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter") handleSubmit(e)
        }

        const muiGeneratedEndAdornment = params.InputProps.endAdornment as JSX.Element | undefined
        const muiGeneratedEndAdronmentChildren: JSX.Element[] | undefined = muiGeneratedEndAdornment?.props.children

        return (
          <PopperTextInputForm>
            <PopperTextInput
              value={value}
              onKeyPress={handleKeyPress}
              variant="outlined"
              helperText={helperText}
              label={inputLabel}
              $lightMode={lightMode}
              $minHeight={minHeight}
              onChange={onInputChangeInternal}
              {...params}
              InputProps={{
                ...params.InputProps,
                startAdornment: multiple ? params.InputProps.startAdornment : startAdornment,
                endAdornment: (
                  <WrapperEndAdornment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {muiGeneratedEndAdronmentChildren?.map((child) => child)}
                    <CustomSaveButton onClick={handleSubmit} data-cy="saveBtn">
                      {confirmText}
                    </CustomSaveButton>
                  </WrapperEndAdornment>
                ),
              }}
            />
          </PopperTextInputForm>
        )
      } else {
        return (
          <PopperTextInput
            value={value}
            variant="outlined"
            label={inputLabel}
            helperText={helperText}
            $lightMode={lightMode}
            $minHeight={minHeight}
            onChange={onInputChangeInternal}
            {...params}
            InputProps={{
              ...params.InputProps,
              startAdornment: multiple ? params.InputProps.startAdornment : startAdornment,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )
      }
    }

    const renderSingleDropdown = () => {
      const selectedValue = selectedValues[0] || value
      const internalOnChange = (_: SyntheticEvent, newValue: DropdownT | null) => {
        setSelectedValues(newValue ? [newValue] : [])
        onChange && (onChange as (newValue: DropdownT[]) => Promise<void> | void)(newValue ? [newValue] : [])
      }

      return (
        <>
          <Autocomplete
            sx={{
              "& .MuiAutocomplete-popupIndicator": {
                ...(!lightMode && { color: "white" }),
              },
              "& .MuiAutocomplete-clearIndicator": {
                ...(!lightMode && { color: "white" }),
              },
            }}
            autoHighlight
            disableClearable={disableClearable}
            isOptionEqualToValue={(o: DropdownT, v: DropdownT) => v === o}
            options={options}
            value={selectedValue}
            getOptionLabel={getOptionLabel}
            openOnFocus={false}
            renderInput={renderInput}
            PaperComponent={renderPaper}
            onChange={internalOnChange}
            renderOption={internalRenderOption}
            loading={loading}
          />
          {!isSaveButtonInsideInputField && <PopperSaveButton onClick={handleSubmit}>{confirmText}</PopperSaveButton>}
        </>
      )
    }

    const renderMultiDropdown = () => {
      const internalOnChange = (_: SyntheticEvent, newValue: DropdownT[]) => {
        setSelectedValues(newValue)
        onChange && (onChange as (newValue: DropdownT[]) => Promise<void> | void)(newValue)
      }

      const internalRenderTags = (values: DropdownT[], getTagProps: Function) => {
        if (!props.multiple) return

        const { renderTag } = props
        if (renderTag) {
          return values.map((newValue, index) => renderTag(newValue, getTagProps({ index })))
        }
        return values.map((newValue, index) => <Chip label={formatOption(newValue)} {...getTagProps({ index })} />)
      }

      return (
        <>
          <Autocomplete
            sx={{
              "& .MuiAutocomplete-popupIndicator": {
                ...(!lightMode && { color: "white" }),
              },
            }}
            autoHighlight={true}
            value={selectedValues}
            options={options}
            openOnFocus={false}
            getOptionLabel={getOptionLabel}
            multiple
            renderInput={renderInput}
            PaperComponent={renderPaper}
            onChange={internalOnChange}
            renderTags={internalRenderTags}
            renderOption={internalRenderOption}
            loading={loading}
          />
          {!isSaveButtonInsideInputField && <PopperSaveButton onClick={handleSubmit}>{confirmText}</PopperSaveButton>}
        </>
      )
    }

    return multiple ? renderMultiDropdown() : renderSingleDropdown()
  }

  return (
    <ThemeProvider theme={lightMode ? PopperLightTheme : PopperDarkTheme}>
      <Popper
        title={popperTitle}
        onClose={internalOnClose}
        onDelete={onDelete}
        anchorEl={anchorEl}
        className={className}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        popperAddNew={dropdownNotFoundHook?.isAddNew ? dropdownNotFoundHook.popperAddNewProps : undefined}
      >
        {dropdownNotFoundHook?.isAddNew && dropdownNotFoundHook.renderAddNewPopper()}
        {!dropdownNotFoundHook?.isAddNew && renderDropdown()}
      </Popper>
    </ThemeProvider>
  )
}

const WrapperEndAdornment = styled.div`
  position: absolute;
  right: 6px;
  display: flex;
  align-items: center;
`
const PopperTextInputForm = styled.div`
  position: relative;
`

const CustomSaveButton = styled(PopperSaveButton)`
  width: unset;
  margin: 0;
`

const CustomPaper = styled(Paper)<{ $isLightMode: boolean }>`
  // intentionally not through theme since this is to mimic mui behaviour
  padding-bottom: 8px;

  ${(p) =>
    !p.$isLightMode &&
    css`
      background-color: ${theme.color.black6};
      div {
        color: white;
      }
    `}

  ul {
    padding-bottom: 0;
    li {
      ${(p) =>
        !p.$isLightMode &&
        css`
          color: white;

          :hover {
            background-color: ${theme.color.black4} !important;
          }
        `}
    }
  }
`
